import { Component, Input, Output, EventEmitter, AfterViewInit, OnDestroy, OnChanges, signal } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { createForm, FormType, subformComponentProviders } from 'ngx-sub-form';
import { catchError, map } from 'rxjs';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { Flow } from 'src/app/perical/models/flow.model';
import { Fluid, FluidType } from 'src/app/perical/models/fluid.model';
import { DrillingOperation, MudPit, OperationType } from 'src/app/perical/models/thermal-operation.model';
import { GetShortLengthValueFromInches } from 'src/app/perivis/shared/helpers/units.helper';
import { WellType } from 'src/app/shared/models/wellType.model';
import { PeriforOnChangeMessages, SignalRService } from 'src/app/shared/services/signal-r.service';
import { DrillstringService } from 'src/app/wellbore-inputs/services/drillstring.service';

export interface DrillingOperationForm {
  type: OperationType;
  fluid: Fluid;
  standardFluidFlow: Flow;
  includeRiser: boolean;
  outsideDiameter: number;
  insideDiameter: number;
  startDepth: number;
  endDepth: number;
  inletTemperature: number;
  totalFlowArea: number;
  annulusIndex: number;
  isPoolOutOfHole: boolean;
  poolOutOfHoleTime: number;
  isConditionHole: boolean;
  conditionHoleTime: number;
  isRunInHole: boolean;
  runInHoleTime: number;
  useMudPits: boolean;
  mudPits: any;
}

@Component({
  selector: 'app-drilling-operation-details',
  templateUrl: './drilling-operation-details.component.html',
  providers: subformComponentProviders(DrillingOperationDetailsComponent),
  standalone: false
})
export class DrillingOperationDetailsComponent implements OnChanges, OnDestroy, AfterViewInit {

  constructor(
    private _drillstringService: DrillstringService,
    private _signalRService: SignalRService
  ) { }

  public fluids: Fluid[];
  @Input('fluids') set _fluids(fluids: Fluid[]) {
    this.fluids = fluids?.filter(f => f.state.type === FluidType.BRINE ||
      f.state.type === FluidType.STANDARDMUD ||
      f.state.type === FluidType.ADVANCEDMUD)
      .sort((a, b) => a.state.name.localeCompare(b.state.name));
  }

  @Input()
  private annularFluidId: string;

  @Input()
  private wellConfig: any;

  @Input()
  public wellType: WellType;

  @Input()
  public isReadOnly: boolean;

  @Input()
  public surfaceTemperature: number;

  @Input()
  public currentStringId: string;

  @Input()
  public defaultRiser: any;

  @Input()
  public userUnits: UserUnitsModel;

  @Input()
  public triggerMudPitTempChange: boolean;

  @Output()
  drillingRangeChanged = new EventEmitter<{ startDepth: number, endDepth: number }>();

  @Output()
  selectedTabChange = new EventEmitter<string>();

  @Input()
  currentTubularIndex: number;

  @Input()
  public previousOperationMudPits: MudPit;

  public operationType = 'drilling';
  public FluidType: typeof FluidType = FluidType;
  public isLoading = signal<boolean>(true);
  public startDrillingMd: number;
  public endDrillingMd: number;
  public selectedDrillPipe: string;
  public drillPipe: any;
  public hwdp: any;
  public collar: any;
  public drillPipeLength: number;
  public selectedHwdp: string;
  public selectedCollar: string;
  public hasHwdp: boolean;
  public hasCollar: boolean;
  public drillpipeMissing: boolean;
  public showRiser: boolean;
  public conditionHoleDisabled: boolean;
  public poohDisabled: boolean;
  public rihDisabled: boolean;
  public totalFlowAreaValidation: { min: number, max: number };
  public temperatureValidation: { min: number, max: number };
  public tempUnit: string;
  public shortLengthUnit: string;
  public pipeRoughnessUnit: string;
  public weigthUnit: string;
  public areaUnit: string;
  public useMudPits = false;
  private _dataBackup: any | null = null;
  private _drillString: any;

  public form = createForm<DrillingOperation, DrillingOperationForm>(this, {
    formType: FormType.SUB,
    formControls: {
      type: new UntypedFormControl(OperationType.DRILLING),
      fluid: new UntypedFormControl(null, [Validators.required]),
      startDepth: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required] }),
      endDepth: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required] }),
      includeRiser: new UntypedFormControl(null),
      outsideDiameter: new UntypedFormControl(null, { updateOn: 'blur' }),
      insideDiameter: new UntypedFormControl(null, { updateOn: 'blur' }),
      standardFluidFlow: new UntypedFormControl(null, [Validators.required]),
      inletTemperature: new UntypedFormControl(null, { updateOn: 'blur' }),
      totalFlowArea: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required] }),
      isPoolOutOfHole: new UntypedFormControl(false),
      poolOutOfHoleTime: new UntypedFormControl(0, { updateOn: 'blur', validators: [Validators.required, Validators.min(0), Validators.max(100000)] }),
      isConditionHole: new UntypedFormControl(false),
      conditionHoleTime: new UntypedFormControl(0, { updateOn: 'blur', validators: [Validators.required, Validators.min(0), Validators.max(100000)] }),
      isRunInHole: new UntypedFormControl(false),
      runInHoleTime: new UntypedFormControl(0, { updateOn: 'blur', validators: [Validators.required, Validators.min(0), Validators.max(100000)] }),
      useMudPits: new UntypedFormControl(false),
      mudPits: new UntypedFormControl(null),
      annulusIndex: new UntypedFormControl(null)
    },
    toFormGroup: (obj: DrillingOperation | null): DrillingOperationForm | null => {
      if (!obj) {
        return null;
      }

      this._dataBackup = { ...obj };

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { fluid, flow, riser, startDepth, endDepth, inletTemperature, totalFlowArea, useMudPits, mudPits, includeRiser,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
        isPoolOutOfHole, poolOutOfHoleTime, isConditionHole, conditionHoleTime, isRunInHole, runInHoleTime, ...commonValues } = obj;

      obj.fluid = this.fluids?.find(f => f.id === obj.fluid.id);
      this.useMudPits = obj.useMudPits;

      this.rihChange(isRunInHole, false, runInHoleTime > 0);
      this.circOnBottomChange(isConditionHole);
      this.poohChange(isPoolOutOfHole);
      this.showRiser = includeRiser;

      return {
        type: obj.type,
        standardFluidFlow: obj.flow,
        includeRiser: includeRiser,
        outsideDiameter: riser?.outsideDiameter,
        insideDiameter: riser?.insideDiameter,
        fluid: obj.fluid,
        startDepth,
        endDepth,
        inletTemperature,
        totalFlowArea,
        isPoolOutOfHole,
        poolOutOfHoleTime,
        isConditionHole,
        conditionHoleTime,
        isRunInHole,
        runInHoleTime,
        useMudPits: useMudPits,
        mudPits: useMudPits ? mudPits : null,
        annulusIndex: obj.annulusIndex
      };
    },
    fromFormGroup: (formValue: DrillingOperationForm): DrillingOperation | null => {
      const { fluid, standardFluidFlow, includeRiser, outsideDiameter, insideDiameter, startDepth, endDepth,
        inletTemperature, totalFlowArea, isPoolOutOfHole, poolOutOfHoleTime, isConditionHole,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        isRunInHole, runInHoleTime, conditionHoleTime, useMudPits, mudPits, annulusIndex } = formValue;

        const controls = this.form.formGroup.controls;
        const check = (control, field) => control?.invalid || (control?.touched && this._dataBackup?.[field] === formValue[field]);

        if (check(controls.startDepth, 'startDepth') ||
            check(controls.endDepth, 'endDepth') ||
            check(controls.totalFlowArea, 'totalFlowArea') ||
            check(controls.poolOutOfHoleTime, 'poolOutOfHoleTime') ||
            check(controls.conditionHoleTime, 'conditionHoleTime') ||
            check(controls.standardFluidFlow, 'standardFluidFlow') ||
            check(controls.runInHoleTime, 'runInHoleTime')) {
          
          controls.startDepth.markAsUntouched();
          controls.endDepth.markAsUntouched();
          controls.totalFlowArea.markAsUntouched();
          controls.poolOutOfHoleTime.markAsUntouched();
          controls.conditionHoleTime.markAsUntouched();
          controls.standardFluidFlow.markAsUntouched();
          controls.runInHoleTime.markAsUntouched();
          return;
        }

        this._dataBackup = formValue;

      return {
        type: OperationType.DRILLING,
        flow: standardFluidFlow,
        includeRiser,
        riser: this.showRiser ? {
          outsideDiameter,
          insideDiameter
        } : null,
        fluid,
        startDepth,
        endDepth,
        inletTemperature,
        totalFlowArea,
        isPoolOutOfHole,
        poolOutOfHoleTime,
        isConditionHole,
        conditionHoleTime,
        isRunInHole,
        runInHoleTime,
        useMudPits,
        mudPits,
        annulusIndex
      };
    }
  });

  async ngOnChanges(): Promise<void> {
    this.isLoading.set(true);

    this.tempUnit = this.userUnits.temperature;
    this.shortLengthUnit = this.userUnits.shortLengths == 'in' ? '"' : this.userUnits.shortLengths;
    this.weigthUnit = this.userUnits.linearDensity == 'lb/ft' ? '#' : this.userUnits.linearDensity;
    this.pipeRoughnessUnit = this.userUnits.shortLengths;
    this.areaUnit = this.userUnits.area;

    switch (this.tempUnit) {
      case '°F':
        this.temperatureValidation = { min: 0, max: 500 };
        break;
      case '°C':
        this.temperatureValidation = { min: -17.77, max: 260 };
        break;
      case 'K':
        this.temperatureValidation = { min: 255.37, max: 533.15 };
        break;
    }
    this.form.formGroup.controls.inletTemperature.setValidators([Validators.min(this.temperatureValidation.min), Validators.max(this.temperatureValidation.max), Validators.required]);

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));

    if (this.areaUnit == 'in²') {
      this.totalFlowAreaValidation = { min: 0, max: 100 };
    } else {
      this.totalFlowAreaValidation = { min: 0, max: 254 };
    }
    this.form.formGroup.controls.totalFlowArea.setValidators([Validators.min(this.totalFlowAreaValidation.min), Validators.max(this.totalFlowAreaValidation.max), Validators.required]);

    this.getDrillstringData(false);
  }

  signalRfunc(data: any) {
    if ((data.tubularStringId && data.tubularStringId !== this.currentStringId)) {
      return;
    }
    if (data.action === PeriforOnChangeMessages.REFRESH_DRILL_STRING) {
      this.getDrillstringData(true);
    }
  }

  ngAfterViewInit(): void {
    const defaultFluid = this.fluids ? this.fluids.find(x => x.id === this.annularFluidId) : null;
    if (!this.form.formGroup.controls.fluid.value) {
      this.form.formGroup.controls.fluid.setValue(defaultFluid, { emitEvent: false });
    }

    if (this.defaultRiser?.includeRiser && this.form.formGroup.get('includeRiser').value == null) {
      this.form.formGroup.get('includeRiser').setValue(true, { emitEvent: false });
      this.showRiser = true;
      this.checkRiserValues();
    }
  }

  private getDrillstringData(calledFromSignalR: boolean) {
    this.isLoading.set(true);
    this._drillstringService.getDrillstringForTubular().pipe(
      map((drillstring) => {
        this.drillPipe = drillstring['drillPipe'];
        this.hwdp = drillstring['heavyWeightDrillPipe'];
        this.collar = drillstring['collar'];
        this.drillpipeMissing = !this.drillPipe && (drillstring.hwdpLength ?? 0) == 0 && (drillstring.collarLength ?? 0) == 0;
        this._drillString = drillstring;

        const mudlineDepth = +(this.wellType.drillFloorElevation + this.wellType.waterDepth);
        this.startDrillingMd = +(this.currentTubularIndex == 0 ? this.wellType.type == 'Land' ? this.wellType.drillFloorElevation : mudlineDepth : this.wellConfig[this.currentTubularIndex - 1].shoeMd);
        this.endDrillingMd = +(this.wellConfig[this.currentTubularIndex].shoeMd);

        this.drillPipeLength = +((this.form.formGroup.controls.endDepth.value ?? this.endDrillingMd) - drillstring.collarLength - drillstring.hwdpLength).toFixed(2);
        this.selectedDrillPipe = this.drillPipe ?
          `Drill Pipe: OD: ${this.drillPipe.outsideDiameter}${this.shortLengthUnit}, Weight: ${this.drillPipe.weightPerFoot.toFixed(2)}${this.weigthUnit}, ID: ${this.drillPipe.insideDiameter}${this.shortLengthUnit}, Grade: ${drillstring.drillPipeGrade?.name || 'Steel'}, Length: ${this.drillPipeLength}${this.userUnits.longLengths}` :
          null;

        this.selectedHwdp = this.hwdp ? `HWDP: OD: ${this.hwdp.outsideDiameter}${this.shortLengthUnit}, Weight: ${this.hwdp.weightPerFoot.toFixed(2)}${this.weigthUnit}, ID: ${this.hwdp.insideDiameter}${this.shortLengthUnit}, Grade: ${drillstring.drillPipeGrade?.name || 'Steel'}, Length: ${drillstring.hwdpLength.toFixed(2)}${this.userUnits.longLengths}` : null;
        this.selectedCollar = this.collar ? `Collar: OD: ${this.collar.outsideDiameter}${this.shortLengthUnit}, Weight: ${this.collar.weightPerFoot.toFixed(2)}${this.weigthUnit}, ID: ${this.collar.insideDiameter}${this.shortLengthUnit}, Grade: ${drillstring.drillPipeGrade?.name || 'Steel'}, Length: ${drillstring.collarLength.toFixed(2)}${this.userUnits.longLengths}` : null;
        this.hasHwdp = drillstring.hwdpLength > 0;
        this.hasCollar = drillstring.collarLength > 0;

        if (!this.hasCollar && !this.hasHwdp && !this.drillPipe) {
          this.selectedDrillPipe = 'Please specify a drill string';
        }

        if (calledFromSignalR) {
          return;
        }

        const hasRiserDefault = this.wellType.type == 'Subsea' && this.currentTubularIndex > 0 ? true : false;
        const riserControl = this.form.formGroup.controls.includeRiser;
        if (riserControl.value === null) {
          riserControl.setValue(hasRiserDefault, { emitEvent: false });
          this.hasRiserChange();
        }

        this.form.formGroup.controls.startDepth
          .setValue((this.form.formGroup.value.startDepth ?? this.startDrillingMd), { emitEvent: false });

        this.form.formGroup.controls.endDepth
          .setValue((this.form.formGroup.value.endDepth ?? this.endDrillingMd), { emitEvent: false });

        this.form.formGroup.controls.inletTemperature
          .setValue((this.form.formGroup.value.inletTemperature ?? this.surfaceTemperature), { emitEvent: false });

        this.form.formGroup.controls.totalFlowArea
          .setValue((this.form.formGroup.value.totalFlowArea ? this.form.formGroup.value.totalFlowArea : this.areaUnit == 'in²' ? 1 : 6.45), { emitEvent: false });

        this.form.formGroup.controls.outsideDiameter
          .setValue((this.form.formGroup.value.outsideDiameter ?? GetShortLengthValueFromInches(21, this.userUnits.shortLengths)), { emitEvent: false });

        this.form.formGroup.controls.insideDiameter
          .setValue((this.form.formGroup.value.insideDiameter ?? GetShortLengthValueFromInches(19.75, this.userUnits.shortLengths)), { emitEvent: false });

        this.drillRangeValidation();

        this.drillingRangeChanged.emit({ startDepth: this.form.formGroup.value.startDepth, endDepth: this.form.formGroup.value.endDepth });

        this.rihChange(this.form.formGroup.controls.isRunInHole, false, this.form.formGroup.controls.runInHoleTime.value > 0);
        this.circOnBottomChange(this.form.formGroup.controls.isConditionHole);
        this.poohChange(this.form.formGroup.controls.isPoolOutOfHole);

        this.isLoading.set(false);
      }),
      catchError(err => {
        this.isLoading.set(false);
        return err;
      })).subscribe();
  }

  private getDrillingRange(startDepth: number | null, endDepth: number | null): { start: { min: number, max: number }, end: { min: number, max: number } } {
    const minInterval = 10;
    return {
      start: {
        min: this.startDrillingMd,
        max: endDepth == null ? this.endDrillingMd : Math.min(this.endDrillingMd, endDepth - minInterval)
      },
      end: {
        min: Math.max(this.startDrillingMd, startDepth ?? this.startDrillingMd) + minInterval,
        max: this.endDrillingMd
      }
    };
  }

  public get drillingRange() {
    const { startDepth, endDepth } = this.form.formGroup.controls;
    return this.getDrillingRange(startDepth.value, endDepth.value);
  }

  public drillRangeValidation(startDepth?: number, endDepth?: number) {
    const { startDepth: startControl, endDepth: endControl } = this.form.formGroup.controls;
    const { start, end } = this.getDrillingRange(startDepth ?? startControl.value, endDepth ?? endControl.value);

    startControl.setValidators([Validators.required, Validators.min(start.min), Validators.max(start.max)]);
    endControl.setValidators([Validators.required, Validators.min(end.min), Validators.max(end.max)]);

    startControl.updateValueAndValidity({ emitEvent: false });
    endControl.updateValueAndValidity({ emitEvent: false });
  }

  onDepthRangeChange(e, depthLocation) {
    const startDepth = depthLocation == 'startDepth' ? e.value : this.form.formGroup.controls.startDepth.value;
    const endDepth = depthLocation == 'endDepth' ? e.value : this.form.formGroup.controls.endDepth.value;

    this.drillingRangeChanged.emit({ startDepth: startDepth, endDepth: endDepth });

    this.drillRangeValidation(startDepth, endDepth);
  }

  public endDepthChanged(e) {
    this.onDepthRangeChange(e, 'endDepth');
    this.drillPipeLength = e.value - this._drillString.collarLength - this._drillString.hwdpLength;
    this.selectedDrillPipe = this.drillPipe ?
      `Drill Pipe: OD: ${this.drillPipe.outsideDiameter.toFixed(2)}${this.shortLengthUnit}, Weight:  ${this.drillPipe.weightPerFoot.toFixed(2)}${this.weigthUnit}, ID:  ${this.drillPipe.insideDiameter.toFixed(2)}${this.shortLengthUnit}, Grade: ${this.drillPipe.drillPipeGrade?.name || 'Steel'}, Length:  ${this.drillPipeLength.toFixed(2)} ${this.userUnits.longLengths}` :
      null;
  }

  public rihChange(e, checkboxChange, greaterThanZero?) {
    if (checkboxChange || (!checkboxChange && (e?.checked ?? e?.value ?? false)) || greaterThanZero) {
      this.rihDisabled = false;
    } else {
      this.rihDisabled = true;
    }

    if (checkboxChange && !e?.checked) {
      this.form.formGroup.controls.runInHoleTime.setValue(0);
    } else if (checkboxChange && e?.checked) {
      this.form.formGroup.controls.runInHoleTime.setValue(10);
    }

    this.drillRangeValidation();
  }

  public circOnBottomChange(e) {
    const checked = e?.checked ?? e?.value ?? e;
    if (checked) {
      this.conditionHoleDisabled = false;
    } else {
      this.conditionHoleDisabled = true;
    }

    if (!checked && this.form.formGroup.controls.conditionHoleTime.value > 0) {
      this.form.formGroup.controls.conditionHoleTime.setValue(0);
    } else if (checked && this.form.formGroup.controls.conditionHoleTime.value == 0) {
      this.form.formGroup.controls.conditionHoleTime.setValue(10);
    }
  }

  public poohChange(e: any): void {
    const checked = e?.checked ?? e?.value ?? e;
    if (checked) {
      this.poohDisabled = false;
    } else {
      this.poohDisabled = true;
    }

    if (!checked && this.form.formGroup.controls.poolOutOfHoleTime.value > 0) {
      this.form.formGroup.controls.poolOutOfHoleTime.setValue(0);
    } else if (checked && this.form.formGroup.controls.poolOutOfHoleTime.value == 0) {
      this.form.formGroup.controls.poolOutOfHoleTime.setValue(10);
    }
  }

  public hasRiserChange() {
    this.showRiser = this.form.formGroup.controls.includeRiser.value ?? false;

    if (this.showRiser) {
      this.form.formGroup.controls.outsideDiameter.setValidators([Validators.required]);
      this.form.formGroup.controls.insideDiameter.setValidators([Validators.required]);
    } else {
      this.form.formGroup.controls.outsideDiameter.clearValidators();
      this.form.formGroup.controls.insideDiameter.clearValidators();
    }

    this.form.formGroup.controls.outsideDiameter.updateValueAndValidity();
    this.form.formGroup.controls.insideDiameter.updateValueAndValidity();

    this.checkRiserValues();
  }

  private checkRiserValues() {
    this.form.formGroup.controls.outsideDiameter
      .setValue((this.form.formGroup.value.outsideDiameter ?? this.defaultRiser?.riser?.outsideDiameter ?? GetShortLengthValueFromInches(21, this.userUnits.shortLengths)), { emitEvent: false });

    this.form.formGroup.controls.insideDiameter
      .setValue((this.form.formGroup.value.insideDiameter ?? this.defaultRiser?.riser?.insideDiameter ?? GetShortLengthValueFromInches(19.75, this.userUnits.shortLengths)));
  }

  ngOnDestroy(): void {
    this.signalRfunc = null;
  }
}
